import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Reveal from "@/components/common/Reveal";
import ProjectPlaceholder from "@/components/common/ProjectPlaceholder";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/projects/${slug}`);
        setProject(data);
        const all = await api.get("/projects", { params: { limit: 8 } });
        setRelated(all.data.filter((p) => p.slug !== slug).slice(0, 3));
      } catch (e) {
        setError("Project not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return <div className="pt-40 pb-20 text-center mono-label text-zinc-500">Loading…</div>;
  }
  if (error || !project) {
    return (
      <div className="pt-40 pb-20 text-center">
        <div className="mono-label text-zinc-500 mb-4">404</div>
        <h1 className="font-display text-4xl">{error || "Project not found"}</h1>
        <Link to="/portfolio" className="btn-outline mt-10 inline-flex">
          Back to Portfolio <ArrowLeft className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* HERO */}
      <section className="pt-20 md:pt-24 pb-6 md:pb-10 max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14" data-testid="project-detail-hero">
        <Link to="/portfolio" className="mono-label text-zinc-500 hover:text-black inline-flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> Back to Portfolio
        </Link>
        <div className="mono-label text-zinc-500 mt-8">{project.category} · {project.year}</div>
        <h1 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-4 max-w-4xl">
          {project.title}
        </h1>
      </section>

      {/* COVER */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 mb-14 md:mb-16">
        <div className="aspect-[21/10] bg-zinc-100 overflow-hidden">
          {project.cover_image ? (
            <img src={project.cover_image} alt={project.title} className="w-full h-full object-cover" />
          ) : (
            <ProjectPlaceholder title={project.title} category={project.category} className="w-full h-full" />
          )}
        </div>
      </section>

      {/* META + DESCRIPTION */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-8 pb-16">
        <div className="lg:col-span-4 space-y-6 border-t border-zinc-200 pt-6">
          <div>
            <div className="mono-label text-zinc-500">Client</div>
            <div className="text-base mt-1.5">{project.client || "—"}</div>
          </div>
          <div>
            <div className="mono-label text-zinc-500">Location</div>
            <div className="text-base mt-1.5">{project.location || "—"}</div>
          </div>
          <div>
            <div className="mono-label text-zinc-500">Year</div>
            <div className="text-base mt-1.5">{project.year || "—"}</div>
          </div>
          <div>
            <div className="mono-label text-zinc-500">Scope</div>
            <div className="text-base mt-1.5">{project.scope || "—"}</div>
          </div>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 border-t border-zinc-200 pt-6">
          <p className="text-lg md:text-xl font-display leading-[1.4] text-zinc-800 text-balance">
            {project.description}
          </p>
        </div>
      </section>

      {/* GALLERY */}
      {project.gallery && project.gallery.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 pb-16 md:pb-24">
          <div className="mono-label text-zinc-500 mb-6">Gallery</div>
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            {project.gallery.map((g, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className={`aspect-[4/3] bg-zinc-100 overflow-hidden img-hover-scale ${i % 3 === 0 ? "md:col-span-2 md:aspect-[21/9]" : ""}`}>
                  {g && <img src={g} alt={`${project.title} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />}
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* RELATED */}
      {related.length > 0 && (
        <section className="bg-[#F9F9F8] border-t border-zinc-200 py-16">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
            <div className="mono-label text-zinc-500 mb-8">More Projects</div>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/portfolio/${p.slug}`} className="block group" data-testid={`related-project-${p.slug}`}>
                  <div className="img-hover-scale aspect-[4/3] bg-white">
                    {p.cover_image ? (
                      <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <ProjectPlaceholder title={p.title} category={p.category} className="w-full h-full" />
                    )}
                  </div>
                  <div className="mt-3">
                    <div className="mono-label text-zinc-500">{p.category}</div>
                    <h3 className="font-display text-lg md:text-xl mt-1.5 group-hover:text-[#BE1E2D] transition-colors">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/portfolio" className="btn-outline inline-flex">
                All Projects <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
